import { useMemo, useState } from 'react';
import { BaseRecord, SortableColumn } from '../types';
import { DataTableSortStatus } from 'mantine-datatable';

function getValueByKey(obj: Record<string, unknown>, key: string): unknown {
  if (!key.includes('.')) {
    return obj[key];
  }
  const parts = key.split('.');
  let value: unknown = obj;
  for (const part of parts) {
    value = value ? (value as Record<string, unknown>)[part] : undefined;
  }
  return value;
}

export default function useSortableData<T extends BaseRecord>(
  data: T[],
  columns: SortableColumn<T>[],
  initialSort?: { columnAccessor: string; direction: 'asc' | 'desc' },
) {
  const defaultColumn = initialSort
    ? columns.find(c => c.accessor === initialSort.columnAccessor)
    : columns.find(c => c.sortable);

  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<T>>({
    columnAccessor: defaultColumn?.accessor ?? '',
    sortKey: defaultColumn?.sortKey ?? (defaultColumn?.accessor as string) ?? '',
    direction: initialSort?.direction ?? 'asc',
  });

  const sortedData = useMemo(() => {
    const activeColumn = columns.find(c => c.accessor === sortStatus.columnAccessor);
    const sortValueGetter = activeColumn?.sortValueGetter;
    const effectiveSortKey =
      sortStatus.sortKey || activeColumn?.sortKey || sortStatus.columnAccessor;
    if (!effectiveSortKey && !sortValueGetter) {
      return data;
    }

    return [...data].sort((a, b) => {
      const aValue = sortValueGetter
        ? sortValueGetter(a)
        : getValueByKey(a as Record<string, unknown>, effectiveSortKey as string);
      const bValue = sortValueGetter
        ? sortValueGetter(b)
        : getValueByKey(b as Record<string, unknown>, effectiveSortKey as string);

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortStatus.direction === 'desc' ? bValue - aValue : aValue - bValue;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortStatus.direction === 'desc'
          ? bValue.localeCompare(aValue)
          : aValue.localeCompare(bValue);
      }

      return 0;
    });
  }, [sortStatus, data, columns]);

  return { sortStatus, setSortStatus, sortedData };
}
